import React, { CSSProperties, useState } from 'react';
import { Typography } from '@mui/material';
import { getVisibility } from 'framework/utils';
import { useFormContext, FormFieldsData } from 'framework/form';
import { ScrollToSectionAnchor } from 'framework/ui/Anchor';
import classnames from 'classnames';
import { Box, Grid2 as Grid } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { TooltipSize } from 'framework/ui/mui-builder';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputTooltip from '../input/InputTooltip';

export interface FormSectionProps {
  isVisible?: boolean | ((data: FormFieldsData) => boolean);
  children: React.ReactElement | React.ReactElement[] | React.ReactNode;
  title?: string | React.ReactElement | ((data?: FormFieldsData) => string | React.ReactElement);
  titleId?: string;
  subtitle?: string;
  hasEqualPadding?: boolean;
  hasAnchorLink?: boolean;
  titleStyle?: CSSProperties;
  subtitleClass?: string;
  subtitleSx?: SxProps<Theme>;
  tooltipMessage?: string | React.ReactElement;
  tooltipSource?: string;
  isLargeTooltip?: boolean;
  titleVariant?: 'h4' | 'h5' | 'h6' | 'subtitle1';
  actions?: React.ReactNode;
  isExpandedSection?: boolean;
  defaultExpanded?: boolean;
  isAutoWidth?: boolean;
  formStyle?: CSSProperties;
}

const FormSection = ({
  children,
  title = '',
  titleId,
  subtitle,
  isVisible = true,
  hasAnchorLink = false,
  hasEqualPadding = false,
  titleStyle,
  subtitleClass,
  subtitleSx,
  tooltipMessage,
  tooltipSource,
  isLargeTooltip = false,
  titleVariant = 'h4',
  actions,
  isExpandedSection = false,
  defaultExpanded = false,
  isAutoWidth = false,
  formStyle,
}: FormSectionProps) => {
  const { data } = useFormContext();
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const isSectionsVisible = getVisibility(data, isVisible as any);

  const titleHeader = typeof title === 'function' ? title(data) : title;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    isSectionsVisible && (
      <Box
        component="fieldset"
        className={classnames('long-form-section', {
          'equal-padding': hasEqualPadding,
          'long-form-section-min-width': isAutoWidth,
        })}
        sx={[
          {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'border.separator',
            borderRadius: '4px',
            boxShadow: 0,
            margin: '0 0 30px',
            padding: '22px 20px 4px',
            position: 'relative',
            transition: 'box-shadow 0.2s',
            minWidth: isAutoWidth ? 'auto' : undefined,
            '&:focus-within': { boxShadow: 5 },
            '&:last-of-type': { marginBottom: '15px' },
            '.MuiDialogContent-root &': { border: 0, margin: '0 0 20px' },
          },
          ...(formStyle ? [formStyle] : []),
        ]}
      >
        <Grid container>
          <Grid size={12}>
            {(!!title || hasAnchorLink) && (
              <Box
                component="legend"
                className="section-title"
                sx={[
                  {
                    color: 'primary.main',
                    marginBottom: '20px',
                    padding: 0,
                    '& h4 ~ .copy-anchor': { transform: 'translateY(5px)' },
                  },
                  ...(titleStyle ? [titleStyle] : []),
                ]}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Typography variant={titleVariant} display="flex">
                      {titleHeader}
                      <ScrollToSectionAnchor
                        titleId={titleId || (typeof titleHeader === 'string' ? titleHeader : '')}
                        isLinkDiplayed={hasAnchorLink}
                      />
                    </Typography>
                    {tooltipMessage && (
                      <InputTooltip
                        source={tooltipSource || ''}
                        message={tooltipMessage}
                        tooltipSize={isLargeTooltip ? TooltipSize.BIG : TooltipSize.DEFAULT}
                        sx={{ marginLeft: '5px', fontSize: '20px', transform: 'translateY(3px)' }}
                      />
                    )}

                    {actions && (
                      <Box ml="10px" display="flex" alignItems="center">
                        {actions}
                      </Box>
                    )}
                  </Box>

                  {isExpandedSection && (
                    <Box display="flex" alignItems="center">
                      <IconButton onClick={handleToggle}>
                        <ExpandMoreIcon
                          className={classnames('toggle-icon', {
                            open: isOpen,
                          })}
                          style={{
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {subtitle && (
              <Typography
                variant="body1"
                fontStyle="italic"
                className={classnames('subtitle', subtitleClass)}
                mb={1.75}
                sx={subtitleSx}
              >
                {subtitle}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: (isOpen && isExpandedSection) || !isExpandedSection ? 'block' : 'none' }}>{children}</Box>
      </Box>
    )
  );
};

export default FormSection;
